import React, { useState } from 'react'
import { formatDistanceToNow } from '../utils/dateUtils'
import { getStatusColor, getStatusText, getHotelStatus, getStatusBadgeClass } from '../utils/statusUtils'

function HotelGrid({ data, filters, loading }) {
  // Debug logging
  console.log('HotelGrid - Received data:', data)
  console.log('HotelGrid - Received filters:', filters)
  console.log('HotelGrid - Loading state:', loading)
  
  // Close dropdown when clicking outside
  React.useEffect(() => {
    const handleClickOutside = (event) => {
      if (!event.target.closest('.relative')) {
        // Close all dropdowns by triggering a custom event
        document.dispatchEvent(new CustomEvent('closeDropdowns'))
      }
    }

    document.addEventListener('click', handleClickOutside)
    return () => document.removeEventListener('click', handleClickOutside)
  }, [])
  
  if (loading) {
    return (
      <div className="card">
        <h3 className="text-lg font-semibold text-gray-900 mb-4">Hotel Status</h3>
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
          {[...Array(6)].map((_, i) => (
            <div key={i} className="p-4 border border-gray-200 rounded-lg animate-pulse">
              <div className="h-4 bg-gray-200 rounded w-3/4 mb-2"></div>
              <div className="h-3 bg-gray-200 rounded w-1/2 mb-2"></div>
              <div className="h-3 bg-gray-200 rounded w-2/3"></div>
            </div>
          ))}
        </div>
      </div>
    )
  }

  // More robust data handling
  let hotels = []
  
  console.log('HotelGrid - Raw data structure:', JSON.stringify(data, null, 2))
  
  if (data && data.hotels) {
    console.log('HotelGrid - data.hotels structure:', JSON.stringify(data.hotels, null, 2))
    
    if (Array.isArray(data.hotels)) {
      // If hotels is directly an array
      hotels = data.hotels
      console.log('HotelGrid - Using direct array')
    } else if (data.hotels.data && Array.isArray(data.hotels.data)) {
      // If hotels is paginated with data property
      hotels = data.hotels.data
      console.log('HotelGrid - Using paginated data array, length:', hotels.length)
    } else if (typeof data.hotels === 'object') {
      // If hotels is an object, try to extract array values
      const hotelValues = Object.values(data.hotels)
      const hotelArray = hotelValues.find(val => Array.isArray(val))
      if (hotelArray) {
        hotels = hotelArray
        console.log('HotelGrid - Using extracted array from object')
      }
    }
  } else if (Array.isArray(data)) {
    // If data itself is an array
    hotels = data
    console.log('HotelGrid - Using data as direct array')
  }

  // Ensure hotels is always an array
  if (!Array.isArray(hotels)) {
    hotels = []
  }

  // Debug final hotels array
  console.log('HotelGrid - Final hotels array:', hotels)
  console.log('HotelGrid - Hotels count:', hotels.length)

  // Apply filters
  let filteredHotels = hotels

  if (filters) {
    // Apply search filter
    if (filters.search && filters.search.trim() !== '') {
      const searchTerm = filters.search.toLowerCase().trim()
      filteredHotels = filteredHotels.filter(hotel => {
        const hotelName = (hotel.title || hotel.name || '').toLowerCase()
        const hotelAddress = (hotel.address || '').toLowerCase()
        return hotelName.includes(searchTerm) || hotelAddress.includes(searchTerm)
      })
    }

    // Apply status filter
    if (filters.status && filters.status !== 'all') {
      filteredHotels = filteredHotels.filter(hotel => {
        // Use real-time status calculation for filtering (same as KPICards)
        const realTimeStatus = getHotelStatus(hotel.last_ping)
        return realTimeStatus === filters.status
      })
    }
  }

  console.log('HotelGrid - Filtered hotels count:', filteredHotels.length)

  return (
    <div className="card">
      <div className="flex justify-between items-center mb-4">
        <h3 className="text-lg font-semibold text-gray-900">Hotel Status</h3>
        <div className="text-sm text-gray-500">
          {filteredHotels.length} of {hotels.length} hotels
        </div>
      </div>
      
      {filteredHotels.length === 0 ? (
        <div className="text-center py-8 text-gray-500">
          {filters && (filters.search || filters.status !== 'all') 
            ? 'No hotels match your filters' 
            : 'No hotels found'
          }
        </div>
      ) : (
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
          {filteredHotels.map((hotel) => (
            <HotelCard key={hotel.id} hotel={hotel} />
          ))}
        </div>
      )}
    </div>
  )
}

function HotelCard({ hotel }) {
  const [showDropdown, setShowDropdown] = useState(false)
  
  // Listen for close dropdown events
  React.useEffect(() => {
    const handleCloseDropdowns = () => {
      setShowDropdown(false)
    }

    document.addEventListener('closeDropdowns', handleCloseDropdowns)
    return () => document.removeEventListener('closeDropdowns', handleCloseDropdowns)
  }, [])
  
  // Map API response properties to component properties
  const hotelName = hotel.title || hotel.name || 'Unknown Hotel'
  
  // Determine real-time status based on last_ping timestamp
  const realTimeStatus = hotel.last_ping ? getHotelStatus(hotel.last_ping) : 'disconnected'
  
  // Use real-time status for display, fallback to API status
  const displayStatus = realTimeStatus
  const statusColor = getStatusColor(displayStatus)
  const statusText = getStatusText(displayStatus)
  const statusBadgeClass = getStatusBadgeClass(displayStatus)
  
  const lastPingText = hotel.last_ping 
    ? formatDistanceToNow(new Date(hotel.last_ping))
    : 'Never'

  // Navigation handlers
  const handleViewHotel = () => {
    const hotelUrl = hotel.url_hotel
    window.open(hotelUrl, '_blank')
    setShowDropdown(false)
  }

  const handleLoginAsHotel = async () => {
    try {
      const baseUrl = 'https://be.liburanterus.com'
      const loginUrl = `${baseUrl}/admin/module/hotel/loginAsHotel/${hotel.id}`
      window.open(loginUrl, '_blank')
      setShowDropdown(false)
    } catch (error) {
      console.error('Error logging in as hotel:', error)
      alert('Failed to login as hotel. Please try again.')
    }
  }

  const handleIconClick = (e) => {
    e.stopPropagation()
    setShowDropdown(!showDropdown)
  }

  const handleCardClick = (e) => {
    // Prevent dropdown from closing when clicking inside the dropdown menu
    if (e.target.closest('.dropdown-menu')) {
      e.stopPropagation()
      return
    }
    // Card click no longer toggles dropdown - only icon click does
  }

  return (
    <div className="relative">
      <div 
        className="p-4 border border-gray-200 rounded-lg transition-shadow hover:shadow-md hover:border-gray-300" 
        onClick={handleCardClick}
      >
        <div className="flex items-center justify-between mb-2">
          <h4 className="font-medium text-gray-900 truncate" title={hotelName}>
            {hotelName}
          </h4>
          <div className="flex items-center gap-2">
            <div className={`status-indicator ${statusColor}`}></div>
            <div className="relative">
              <div className="text-gray-400 hover:text-gray-600 cursor-pointer" onClick={handleIconClick}>
                <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
                </svg>
              </div>
              
              {/* Dropdown Menu positioned relative to icon */}
              {showDropdown && (
                <div className="dropdown-menu absolute top-full right-0 mt-1 bg-white border border-gray-200 rounded-lg shadow-lg z-10 w-48">
                  <div className="py-1">
                    <button
                      onClick={handleViewHotel}
                      className="w-full text-left px-4 py-2 text-sm text-gray-700 hover:bg-gray-100 flex items-center gap-2"
                    >
                      <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M10 6H6a2 2 0 00-2 2v10a2 2 0 002 2h10a2 2 0 002-2v-4M14 4h6m0 0v6m0-6L10 14" />
                      </svg>
                      View Hotel Page
                    </button>
                    <button
                      onClick={handleLoginAsHotel}
                      className="w-full text-left px-4 py-2 text-sm text-gray-700 hover:bg-gray-100 flex items-center gap-2"
                    >
                      <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M11 16l-4-4m0 0l4-4m-4 4h14m-5 4v1a3 3 0 01-3 3H6a3 3 0 01-3-3V7a3 3 0 013-3h7a3 3 0 013 3v1" />
                      </svg>
                      Login to Extranet
                    </button>
                  </div>
                </div>
              )}
            </div>
          </div>
        </div>
      
      <div className="space-y-1 text-sm text-gray-600">
        <div className="flex justify-between">
          <span>PMS Status:</span>
          <span className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium ${statusBadgeClass}`}>
            {statusText}
          </span>
        </div>
        
        <div className="flex justify-between">
          <span>Hotel Status:</span>
          <span className="font-medium">
            {hotel.hotel_status || 'Unknown'}
          </span>
        </div>
        
        <div className="flex justify-between">
          <span>Last ping:</span>
          <span>{lastPingText}</span>
        </div>
        
        {hotel.success_rate !== undefined && hotel.success_rate !== null && (
          <div className="flex justify-between">
            <span>Success rate:</span>
            <span>{hotel.success_rate}%</span>
          </div>
        )}

        {hotel.uptime_percentage !== undefined && hotel.uptime_percentage !== null && (
          <div className="flex justify-between">
            <span>Uptime:</span>
            <span>{hotel.uptime_percentage}%</span>
          </div>
        )}

        {hotel.total_requests !== undefined && hotel.total_requests !== null && (
          <div className="flex justify-between">
            <span>Total requests:</span>
            <span>{hotel.total_requests}</span>
          </div>
        )}
      </div>

      {hotel.address && (
          <div className="mt-2 pt-2 border-t border-gray-100">
            <p className="text-xs text-gray-500 truncate" title={hotel.address}>
              {hotel.address}
            </p>
          </div>
        )}
      </div>
    </div>
  )
}

export default HotelGrid